"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";

type Props = {
  code: string;
  language?: string;
  fileName?: string;
  children: React.ReactNode;
};

const EXTENSIONS: Record<string, string> = {
  ts: "ts",
  tsx: "tsx",
  js: "js",
  jsx: "jsx",
  sh: "sh",
  bash: "sh",
  json: "json",
  html: "html",
  css: "css",
  md: "md",
};

export default function CodeBlock({ code, language, fileName, children }: Props) {
  const [copied, setCopied] = useState(false);

  const extension = language ? (EXTENSIONS[language] ?? language) : "txt";
  const fallbackFileName = `File.${extension}`;

  const handleCopy = async () => {
    if (!code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        throw new Error("Clipboard API indisponible");
      }
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="my-5 overflow-hidden rounded-xl border border-white/10">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-background pl-4 p-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-on-surface-variant">
          {fileName ?? fallbackFileName}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className={`inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-background px-2 py-1 text-[11px] font-mono transition-colors  ${copied ? "text-green-400" : "text-white/70 hover:text-white"}`}
          aria-label="Copier"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? "Copié" : "Copier"}
        </button>
      </div>
      <pre className="bg-background p-4 overflow-x-auto">{children}</pre>
    </div>
  );
}
